// package imports
import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// CSS imports
import './EmailTemplates.css';

// Component imports
import Spinner from '../../components/spinner/Spinner';
import Alert from '../../components/alert/Alert';
import EmailTemplateCard from '../../components/emailTemplateCard/EmailTemplateCard';
import PrimaryButton from '../../components/buttons/primaryButton/PrimaryButton';
import SearchBar from '../../components/searchBar/SearchBar';

// Helper imports
import SectionWrapper from '../../helpers/components/sectionWrapper/SectionWrapper';

// Service imports
import { ApiService } from '../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa6';

// Type imports
import { SectionContentSize } from '../../types/sectionWrapper/SectionContentSize';
import { AlertSize } from '../../types/alert/AlertSize';
import { EmailTemplate } from '../../types/emailTemplates/EmailTemplate';
import { EmailTemplatesResult } from '../../types/emailTemplates/EmailTemplatesResult';

const EmailTemplates: React.FC = () => {
  const [search, setSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');
  const [selectedPage, setSelectedPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [showUnknownError, setShowUnknownError] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const getEmailTemplates = useCallback(
    async (tempTemplates: EmailTemplate[], search: string, page: number) => {
      setIsLoading(true);
      const res: EmailTemplatesResult | null =
        await ApiService.getEmailTemplates(search, page, 9);
      setIsLoading(false);
      if (res) {
        for (const template of res.templates) {
          tempTemplates.push(template);
        }
        setTemplates(tempTemplates);
        setSelectedPage(res.page);
        setPagesCount(res.pages);
      }
    },
    []
  );

  useEffect(() => {
    let urlSearch = searchParams.get('search');
    let finalSearch = urlSearch ? decodeURI(urlSearch) : '';

    setSelectedSearch(finalSearch);

    getEmailTemplates([], finalSearch, 1);
  }, [searchParams, getEmailTemplates]);

  const deleteTemplate = async (id: string, index: number) => {
    const res: boolean = await ApiService.deleteEmailTemplate(id);
    if (res) {
      const tempTemplates = templates.slice(0, templates.length);
      tempTemplates.splice(index, 1);
      setTemplates(tempTemplates);
    } else {
      setShowUnknownError(true);
    }
  };

  const duplicateTemplate = async (id: string) => {
    const res: EmailTemplate | null = await ApiService.duplicateEmailTemplate(
      id
    );
    if (res) {
      const tempTemplates = templates.slice(0, templates.length);
      tempTemplates.unshift(res);
      setTemplates(tempTemplates);
    } else {
      setShowUnknownError(true);
    }
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const changeSearch = async (newSearch: string) => {
    navigate(
      '/email-templates' +
        (newSearch !== '' ? '?search=' + encodeURI(newSearch) : '')
    );
  };

  return (
    <div className='EmailTemplates'>
      <Link to='/email-template/create' className='add-button'>
        <PrimaryButton ReactIcon={FaPlus} />
      </Link>
      <SectionWrapper contentWidth={SectionContentSize.MEDIUM}>
        <div className='content'>
          <h1>Email Templates</h1>
          <SearchBar
            className='search-bar'
            value={search}
            placeholder='Search'
            onChange={onSearchChange}
            triggerSearch={changeSearch}
          />
          <InfiniteScroll
            dataLength={templates.length}
            hasMore={selectedPage !== pagesCount}
            next={() =>
              getEmailTemplates(templates, selectedSearch, selectedPage + 1)
            }
            loader={<Spinner isLoading={true} />}
            className='infinite-scroll-container'
          >
            {templates.map((template: EmailTemplate, index: number) => (
              <EmailTemplateCard
                className='item-card'
                index={index}
                template={template}
                deleteTemplate={deleteTemplate}
                duplicateTemplate={duplicateTemplate}
                key={index}
              />
            ))}
          </InfiniteScroll>
          {!isLoading && templates.length === 0 ? (
            <span className='no-items-found'>No email templates</span>
          ) : null}
        </div>
      </SectionWrapper>
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
    </div>
  );
};

export default EmailTemplates;
